import { gsap } from "gsap";
/**
 * This is default accordion class with a11y functions
 * Please fee free to update this class directly or inherit from it
 */
export default class AccordionGrid {
  constructor(elem, APP) {
    this.elem = elem;
    this.triggers = this.elem.querySelectorAll(".js-accgrid-trigger");
    this.panels = this.elem.querySelectorAll(".js-accgrid-panel");

    this.blockContainer = this.elem.querySelector(".js-block-container");
    this.blocks = this.elem.querySelectorAll(".js-block");
    this.blockTriggers = this.elem.querySelectorAll(".js-block-trigger");
    this.blockPanels = this.elem.querySelectorAll(".js-block-panel");
    this.blockTitles = this.elem.querySelectorAll(".js-block-expanded-title");

    this.holder1 = this.elem.querySelector(".js-block-holder-1");
    this.holder2 = this.elem.querySelector(".js-block-holder-2");

    this.accGridDT = this.elem.querySelector(".js-accgrid-dt");
  }

  /**
   * a11y - tabindex
   */
  updateChildElemsTabIndex() {
    this.panels.forEach((panel) => {
      const focusElements = panel.querySelectorAll("a, button");

      if (panel.classList.contains("expanded")) {
        // Disable the tab index to prevent it from receiving focus by keyboard controls.
        focusElements.forEach((elem) => {
          elem.removeAttribute("tabindex");
        });
      } else {
        // Enable the tab index
        focusElements.forEach((elem) => {
          elem.setAttribute("tabindex", -1);
        });
      }
    });
  }

  /**
   * Clicking an accordion trigger opens/collapses the corresponding panel.
   * @param {number} index
   */
  togglePanel(index) {
    const trigger = this.triggers[index];
    const panel = this.panels[index];
    const panelHeight = `${
      panel.querySelector(".js-accgrid-content").offsetHeight
    }px`;
    const targetState = indexPanel.classList.contains("expanded");

    panel.style.height = targetState ? panelHeight : 0;
    panel.setAttribute("aria-hidden", !targetState);
    trigger.setAttribute("aria-expanded", targetState);

    this.updateChildElemsTabIndex();
  }

  /**
   * Clicking an accordion trigger collapses the previously opened panel.
   * @param {number} index
   */
  toggleAllPanels(index) {
    const indexPanel = this.panels[index];
    const panelHeight = `${
      indexPanel.querySelector(".js-accgrid-content").offsetHeight
    }px`;
    const targetState = indexPanel.classList.contains("expanded");

    this.panels.forEach((panel, i) => {
      if (i === index && !targetState) {
        panel.style.height = panelHeight;
        panel.setAttribute("aria-hidden", false);
        panel.classList.add("expanded");
        this.triggers[i].classList.remove("fadeout");
        this.triggers[i].setAttribute("aria-expanded", true);
      } else {
        panel.style.height = 0;
        panel.setAttribute("aria-hidden", true);
        panel.classList.remove("expanded");
        this.triggers[i].classList.add("fadeout");
        this.triggers[i].setAttribute("aria-expanded", false);
      }
    });

    this.updateChildElemsTabIndex();
  }

  /**
   * Set the accordion trigger click handler
   */
  setTriggerHandler() {
    this.triggers.forEach((t, i) => {
      t.addEventListener("click", () => {
        this.toggleAllPanels(i);
      });
    });
  }

  /**
   * Resizing the window updates the opened accordion panel height.
   */
  resizePanel() {
    // mobile accordion
    this.panels.forEach((elem) => {
      const targetHeight = elem.querySelector("div").offsetHeight + "px";
      elem.style.height = elem.classList.contains("expanded")
        ? targetHeight
        : "0";
    });

    // desktop blocks
    this.blocks.forEach((block) => {
      const target = block.querySelector(".js-block-content");
      const targetWidth = this.blockContainer.offsetWidth - 355 - 18;
      target.style.width = `${targetWidth}px`;

      const trigger = block.querySelector(".block-trigger-expanded");
      trigger.style.width = `${targetWidth}px`;

      if (block.classList.contains("expanded")) {
        block.style.width = `${this.blockContainer.offsetWidth - 355}px`;
      }
    });
  }

  /**
   * DESKTOP BLOCKS
   */

  toggleAllBlockPanels(index) {
    // const blockPanel = this.blockPanels[index];
    const targetState = this.blocks[index].classList.contains("expanded");
    let blockEnabled = false;

    let delay = Array.from(this.blocks).find((block) =>
      block.classList.contains("expanded")
    );
    delay = delay ? 0.35 : 0;

    this.blockPanels.forEach((panel, i) => {
      // open
      if (i === index && !targetState) {
        // block
        this.blocks[i].classList.add("expanded");
        this.blockTitles[i].classList.add("expanded");

        // height
        let contentHeight =
          panel.querySelector(".js-block-content").offsetHeight;

        // 60 bottom padding, for scrollbar
        let blockHeight =
          contentHeight + this.blocks[i].querySelector("h3").offsetHeight + 60;

        let overflowedHeight = blockHeight > 580 ? blockHeight - 580 : 0;

        // blockHeight = blockHeight > 580 ? 580 : blockHeight;
        blockHeight = 580;

        // expanded block height
        gsap.to(this.blocks[i], {
          width: this.blockContainer.offsetWidth - 355,
          height: blockHeight,
          duration: 0.35,
          ease: "none",
          delay,
          onComplete: () => {
            // content panel height
            panel.style.height =
              overflowedHeight == 0
                ? `${580 - this.blocks[i].querySelector("h3").offsetHeight - 60}px`
                : `${contentHeight - overflowedHeight}px`;

            // panel
            panel.classList.add("transition-in");
            panel.setAttribute("aria-hidden", false);
            panel.classList.add("expanded");

            // scrollTo
            window.scrollTo({
              top: this.accGridDT.offsetTop - 80,
              left: 0,
              behavior: "smooth",
            });
          },
        });

        // block title
        gsap.to(this.blockTitles[i], {
          opacity: 1,
          duration: 0.35,
          delay: delay,
        });

        // trigger
        this.blockTriggers[i].setAttribute("aria-expanded", true);
        blockEnabled = true;
      } else {
        // close
        panel.style.height = 0;

        gsap.to(this.blocks[i], {
          width: 355,
          height: 280,
          duration: 0.35,
          ease: "none",
          onComplete: () => {
            // scrollTo
            window.scrollTo({
              top: this.accGridDT.offsetTop - 80,
              left: 0,
              behavior: "smooth",
            });
          },
        });

        // block
        this.blocks[i].classList.remove("expanded");

        // panel
        panel.setAttribute("aria-hidden", true);
        panel.classList.remove("expanded");
        panel.classList.remove("transition-in");

        // block title
        this.blockTitles[i].classList.remove("expanded");
        this.blockTitles[i].style.opacity = "0";

        // trigger
        this.blockTriggers[i].setAttribute("aria-expanded", false);
      }
    });

    // 5 blocks layout
    if (this.blocks.length == 5) {
      if (blockEnabled) {
        if (index === 0) {
          this.blockContainer.append(this.blocks[0]);
          this.holder1.append(this.blocks[2], this.blocks[3]);
          this.holder2.append(this.blocks[1], this.blocks[4]);
        }

        if (index === 1) {
          this.blockContainer.append(this.blocks[1]);
          this.holder1.append(this.blocks[0], this.blocks[2]);
          this.holder2.append(this.blocks[3], this.blocks[4]);
        }

        if (index === 2) {
          this.blockContainer.append(this.blocks[2]);
          this.holder1.append(this.blocks[0]);
          this.holder2.append(this.blocks[1], this.blocks[3], this.blocks[4]);
        }

        if (index === 3) {
          this.blockContainer.append(this.blocks[3]);
          this.holder1.append(this.blocks[0], this.blocks[2]);
          this.holder2.append(this.blocks[1], this.blocks[4]);
        }

        if (index == 4) {
          this.blockContainer.append(this.blocks[4]);
          this.holder1.append(this.blocks[0], this.blocks[1]);
          this.holder2.append(this.blocks[2], this.blocks[3]);
        }
      } else {
        while (this.holder1.childNodes.length > 0) {
          this.blockContainer.append(this.holder1.childNodes[0]);
        }

        while (this.holder2.childNodes.length > 0) {
          this.blockContainer.append(this.holder2.childNodes[0]);
        }
      }
    }

    this.blockContainer.setAttribute(
      "data-option",
      blockEnabled ? `opt${index + 1}` : false
    );

    // this.updateChildElemsTabIndex();
  }

  setBlockTriggerHandler() {
    this.blockTriggers.forEach((t, i) => {
      const block = t.parentElement.parentElement;

      t.addEventListener("click", () => {
        this.toggleAllBlockPanels(i);
      });

      t.addEventListener("mouseenter", () => {
        if (!block.classList.contains("expanded")) {
          block.classList.add("mouseenter");
          // t.style.transform = 'scale(1.05)';
        }
      });

      t.addEventListener("mouseleave", () => {
        block.classList.remove("mouseenter");
        // t.style.transform = 'scale(1)';
      });
    });
  }

  init() {
    this.setTriggerHandler();
    this.setBlockTriggerHandler();

    // open first accordion on the page load
    if (this.elem.dataset.openFirst) {
      this.toggleAllPanels(0);
    } else {
      this.updateChildElemsTabIndex();
    }

    window.addEventListener("resize", () => {
      this.resizePanel();
    });

    this.resizePanel();
  }
}
